import secureLocalStorage from "react-secure-storage";
import {
  getHeaders,
  getCurrentUserId,
  toggleOverlay,
  decryptResponse,
  logoutUser,
} from "./apiUtils";

/**
 * Handles GET requests to the API with optional query parameters and loader support.
 * Supports optional blob response parsing.
 */
async function getService(
  endpoint = "",
  method = "GET",
  microService = "identity-manager",
  showLoader = false,
  queryParams: Record<string, any> = {},
  isBlob = false
) {
  try {
    // Show loading overlay if enabled
    toggleOverlay(showLoader);

    // Prepare request headers
    const headers = await getHeaders();

    // Construct full URL with query parameters
    const url = new URL(
      `${process.env.REACT_APP_SERVICE_REGISTRY}service/${microService}/${endpoint}`
    );

    //Without encrytption
    Object.entries(queryParams).forEach(([key, val]) =>
      val != null ? url.searchParams.append(key, String(val)) : null
    );

    // Perform the request
    const response = await fetch(url.toString(), { method, headers });

    // Handle unauthorized access — an expired/invalid token (e.g. after the
    // TO-38233 auth migration) clears the session and bounces to /login instead
    // of leaving a broken screen.
    if (response.status === 401) {
      logoutUser();
      return;
    }

    // Parse the response as blob or JSON
    const data = isBlob ? await response.blob() : await response.json();

    // Decrypt and return the response
    return await decryptResponse(data, isBlob);
  } catch (error) {
    console.error("getService error:", error);
  } finally {
    // Hide loading overlay
    toggleOverlay(false);
  }
}

/**
 * Handles general-purpose API requests (GET, POST, PUT, DELETE).
 * Automatically adds user info, language, and timezone to the request body or query string.
 * Supports encryption and optional blob response.
 */
async function service(
  endpoint = "",
  method = "POST",
  dataObj: any = {},
  showLoader = false,
  microService = "identity-manager",
  encrypt = true,
  isBlob = false
) {
  try {
    // Show loading overlay if enabled
    toggleOverlay(showLoader);

    // Fetch user info from Redux store
    const { userId, role } = getCurrentUserId();

    // Retrieve app language and timezone
    const appLangKey = secureLocalStorage.getItem("site_lang") ?? "en-source";
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Prepare headers
    const headers = await getHeaders();

    // Enrich data with common parameters
    const enrichedData = {
      ...dataObj,
      userId,
      userRole: role,
      appLangKey,
      userTimeZone: timezone,
    };

    // Construct API URL
    let url = `${process.env.REACT_APP_SERVICE_REGISTRY}service/${microService}/${endpoint}`;

    // Set up request options
    const options: RequestInit = {
      method,
      headers,
      mode: "cors",
      credentials: "same-origin",
      redirect: "follow",
    };

    // For GET/HEAD, append parameters to the URL
    if (method === "GET" || method === "HEAD") {
      const params = new URLSearchParams();
      Object.entries(enrichedData).forEach(
        ([k, v]) => v != null && params.append(k, String(v))
      );
      const query = params.toString();
      if (query) url += (url.includes("?") ? "&" : "?") + query;
    } else {
      // Payload sent as plaintext JSON (TO-38233; request/response envelope removed).
      options.body = JSON.stringify(enrichedData);
    }

    // Perform the request
    const response = await fetch(url, options);

    // Handle unauthorized access — an expired/invalid token (e.g. after the
    // TO-38233 auth migration) clears the session and bounces to /login instead
    // of leaving a broken screen.
    if (response.status === 401) {
      logoutUser();
      return;
    }

    // Parse the response as blob or JSON
    const data = isBlob ? await response.blob() : await response.json();
    if (!encrypt) {
      return data;
    }

    // Decrypt and return the response
    return await decryptResponse(data, isBlob);
  } catch (error) {
    console.error("service error:", error);
  } finally {
    // Hide loading overlay
    toggleOverlay(false);
  }
}

/**
 * Sends multipart/form-data (e.g., files) to the API.
 * Accepts FormData as request body.
 */
async function fileSendService(
  endpoint = "",
  method = "POST",
  dataObj: any = {},
  showLoader = false,
  microService = "identity-manager"
) {
  try {
    // Show loading overlay if enabled
    toggleOverlay(showLoader);

    // Prepare headers for form data
    const headers = await getHeaders(false, true);

    // Construct API URL
    let url = `${process.env.REACT_APP_SERVICE_REGISTRY}service/${microService}/${endpoint}`;

    // Set up request options
    const options: RequestInit = {
      method,
      headers,
      mode: "cors",
      credentials: "same-origin",
      redirect: "follow",
    };

    // For GET/HEAD, append query parameters
    if (method === "GET" || method === "HEAD") {
      const params = new URLSearchParams();
      Object.entries(dataObj).forEach(
        ([k, v]) => v != null && params.append(k, String(v))
      );
      const query = params.toString();
      if (query) url += (url.includes("?") ? "&" : "?") + query;
    } else {
      // For other methods, send FormData as body
      options.body = dataObj; // FormData expected
    }

    // Perform the request
    const response = await fetch(url, options);

    // Handle unauthorized access — an expired/invalid token (e.g. after the
    // TO-38233 auth migration) clears the session and bounces to /login instead
    // of leaving a broken screen.
    if (response.status === 401) {
      logoutUser();
      return;
    }

    // Parse and return the response
    return await response.json();
  } catch (error) {
    console.error("fileSendService error:", error);
  } finally {
    // Hide loading overlay
    toggleOverlay(false);
  }
}

/**
 * Exported API methods
 */
export const ApiCall = {
  service,
  getService,
  fileSendService,
};
